import { JsonHalLinks } from "./jsonhal.model";

export enum Environment {
    test = "test",
    development = "development",
    production = "production",
}

export enum Platform {
    workato = "workato",
    powerplatform = "powerplatform",
    salesforce = "salesforce",
    powerBI = "power_bi",
    serviceNow = "service_now",
    chatGptEnterprise = "chat_gpt_enterprise",
}

export enum AuthenticationMethod {
    userpassword = "userpassword",
    apikey = "api-key",
    apitoken = "api-token",
    oauthAuthorizationCode = "oauth-authorization-code",
    oktasso = "okta-saml-sso",
    serviceprincipal = "service-principal",
    certwithuserpassword = "cert-with-userpassword",
    oauthClientCredentialsFlow = "oauth-client-credentials-flow",
    ppZenityMultiTenantAppServicePrincipal = "pp-zenity-multi-tenant-app-service-principal",
}

/**
 * An enum representing assessed resource types.
 * assessed resources are resources that Zenity scans and runs policies on
 */
export enum ResourceType {
    recipe = "recipe",
    flow = "flow",
    connection = "connection",
    environment = "environment",
    dlp_policy = "dlpPolicy",
    portal = "portal",
    portal_page = "portalPage",
    portal_list = "portalList",
    canvas_app = "canvasApp",
    application = "application",
    user = "user",
    organization = "organization",
    connector = "connector",
    custom_connector = "customConnector",
    account = "account",
    project = "project",
    api_client = "apiClient",
    api_collection = "apiCollection",
    api_endpoint = "apiEndpoint",
    on_prem_group = "onPremGroup",
    app = "app",
    tenant = "tenant",
    dataverse_team = "dataverseTeam",
    dataverse_user = "dataverseUser",
    model_driven_app = "modelDrivenApp",
    azure_active_directory_group = "azureActiveDirectoryGroup",
    gateway_cluster = "gatewayCluster",
    gateway = "gateway",
    virtual_connector = "virtualConnector",
    classic_application = "classicApplication",
    lightning_application = "lightningApplication",
    dataverse_table = "dataverseTable",
    copilot = "copilot",
    copilot_topic = "copilotTopic",
    copilot_action = "copilotAction",
    copilot_knowledge = "copilotKnowledge",
    conversational_plugin = "conversationalPlugin",
    connection_plugin = "connectionPlugin",
    flow_plugin = "flowPlugin",
    graph_connection = "graphConnection",
    apex_class = "apexClass",
    object = "object",
    app_page = "appPage",
    site = "site",
    package = "package",
    managed_package = "managedPackage",
    public_group = "publicGroup",
    collaboration_group = "collaborationGroup",
    workspace = "workspace",
    dashboard = "dashboard",
    datasource = "datasource",
    dataset = "dataset",
    report = "report",
    lakehouse = "lakehouse",
    ai_skill = "aiSkill",
    warehouse = "warehouse",
    connected_application = "connectedApplication",
    einstein_bot = "einsteinBot",
    tab = "tab",
    visualforce_page = "visualforcePage",
    lightning_page = "lightningPage",
    apex_trigger = "apexTrigger",
    power_pages_site = "powerPagesSite",
    gpt = "gpt",
    gpt_knowledge = "gptKnowledge",
    gpt_action = "gptAction",
    canvas = "canvas",
}

/**
 * An enum representing resource environment type.
 * folder in workato for example.
 */
export enum ResourceEnvType {
    folder = "folder",
    project = "project",
    environment = "environment",
    workspace = "workspace",
    organization = "organization",
    account = "account",
    none = "none",
}

export enum BusinessCriticality {
    insufficientData = "insufficientData",
    high = "high",
    medium = "medium",
    low = "low",
}

export enum SecurityRisk {
    healthy = "healthy",
    high = "high",
    medium = "medium",
    low = "low",
}

export enum InventoryResourceGenerativeAiOptions {
    createdUsingGenerativeAi = "createdUsingGenerativeAi",
}

export interface ZenityApiError {
    type: string;
    title: string;
    status: number;
    detail: string;
    errorCode: string; // If we need to we can define the enum of error codes to implement dedicated error handling for specific errors
}

export const ResourceTypesPerPlatform: { [platform: string]: ResourceType[] } = {
    [Platform.workato]: [
        ResourceType.recipe,
        ResourceType.connection,
        ResourceType.connector,
        ResourceType.custom_connector,
        ResourceType.api_collection,
        ResourceType.api_endpoint,
        ResourceType.on_prem_group,
        ResourceType.user,
        ResourceType.project,
        // Accounts aren't listed since those resource types aren't usable at time of writing, they aren't even collected with a name
        // API clients aren't listed since those resource types aren't usable at time of writing, they aren't related to API collections/endpoints
        // Custom roles aren't listed since those resource types aren't usable at time of writing, we don't need to represent users RBAC at the moment
    ],
    [Platform.powerplatform]: [
        ResourceType.connection,
        ResourceType.environment,
        ResourceType.flow,
        ResourceType.portal,
        ResourceType.canvas_app,
        ResourceType.user,
        ResourceType.connector,
        ResourceType.custom_connector,
        ResourceType.portal_page,
        ResourceType.portal_list,
        ResourceType.dlp_policy,
        ResourceType.azure_active_directory_group,
        ResourceType.dataverse_team,
        ResourceType.model_driven_app,
        ResourceType.gateway_cluster,
        ResourceType.gateway,
        ResourceType.virtual_connector,
        ResourceType.tenant,
        ResourceType.dataverse_table,
        ResourceType.copilot,
        ResourceType.copilot_topic,
        ResourceType.copilot_action,
        ResourceType.copilot_knowledge,
        ResourceType.conversational_plugin,
        ResourceType.connection_plugin,
        ResourceType.flow_plugin,
        ResourceType.graph_connection,
        ResourceType.power_pages_site,
        // ResourceType.dataverse_user,
        // Dataverse users aren't listed since those resource types aren't supported by Inventory yet
    ],
    [Platform.salesforce]: [
        ResourceType.user,
        ResourceType.flow,
        ResourceType.lightning_application,
        ResourceType.classic_application,
        ResourceType.apex_class,
        ResourceType.organization,
        ResourceType.object,
        ResourceType.site,
        ResourceType.managed_package,
        ResourceType.public_group,
        ResourceType.collaboration_group,
        ResourceType.connected_application,
        ResourceType.einstein_bot,
        ResourceType.tab,
        ResourceType.visualforce_page,
        ResourceType.lightning_page,
        ResourceType.apex_trigger,
    ],
    [Platform.powerBI]: [
        ResourceType.workspace,
        ResourceType.dashboard,
        ResourceType.datasource,
        ResourceType.dataset,
        ResourceType.lakehouse,
        ResourceType.ai_skill,
        ResourceType.warehouse,
        ResourceType.report,
        ResourceType.app,
        ResourceType.user,
        ResourceType.azure_active_directory_group,
    ],
    [Platform.serviceNow]: [ResourceType.user],
    [Platform.chatGptEnterprise]: [ResourceType.user, ResourceType.gpt, ResourceType.gpt_knowledge, ResourceType.gpt_action, ResourceType.canvas],
};

export interface UserActionTimestamps {
    createdAt: Date;
    createdBy: string;
    modifiedAt?: Date;
    modifiedBy?: string;
}

export interface idAndNameModel {
    id: string;
    name: string;
}

export interface LabelModel {
    id: string;
    displayName: string;
    description?: string;
    color: string;
    userActionTimestamps?: UserActionTimestamps;
}

export interface LabelModelWithLinks extends LabelModel, JsonHalLinks {}

export interface CreateLabelModel {
    displayName: string;
    color: string;
    description?: string;
}
export interface AssociatedLabelModel {
    id: string;
    displayName: string;
}

export interface EditLabelModel {
    displayName: string;
    color: string;
}

export interface LabelBindingModel {
    id: string;
    description?: string;
    label: AssociatedLabelModel;
    userActionTimestamps?: UserActionTimestamps;
    mergedLabels?: LabelBindingModel[];
}
